import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BookOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Skeleton, Typography } from 'antd';
import cn from 'classnames';

import { useAntMessage, useSafariKeyboardPatches } from '@/shared/lib/hooks';
import { RichTextEditor } from '@/shared/ui/rich-text-editor';

import {
    useLazyGetAccountCalendarNoteQuery,
    useSaveAccountCalendarNoteMutation,
} from '../../api/trading-platform.api';
import { useGetTradingPlatformSessionId } from '../../models/selectors/get-trading-platform-session-id';
import { useGetTradingPlatformSessionType } from '../../models/selectors/get-trading-platform-session-type';

import cls from './trading-platform-daily-info-as-drawer-notes.module.css';

interface TradingPlatformDailyInfoAsDrawerNotesProps {
    date: DateString;
    className?: string;
}

export const TradingPlatformDailyInfoAsDrawerNotes = memo(
    (props: TradingPlatformDailyInfoAsDrawerNotesProps) => {
        const { date, className } = props;

        const { t } = useTranslation('trading-journal');

        const accountId = useGetTradingPlatformSessionId();
        const connectionType = useGetTradingPlatformSessionType();

        const message = useAntMessage();

        const [getAccountCalendarNoteQuery, { data, isFetching }] =
            useLazyGetAccountCalendarNoteQuery();
        const [saveAccountCalendarNoteMutation, { isLoading, isSuccess }] =
            useSaveAccountCalendarNoteMutation();

        const [isOpened, setIsOpened] = useState<boolean>(false);
        const [value, setValue] = useState<string>();

        const onSave = useCallback(() => {
            if (accountId && connectionType) {
                if (value && new Blob([value]).size / 1024 / 1024 > 4) {
                    message.error?.({
                        content: t('tradingJournalNoteSizeBig'),
                    });
                } else {
                    saveAccountCalendarNoteMutation({
                        id: accountId,
                        connectionType,
                        date,
                        note: value ?? '',
                    });
                }
            }
        }, [
            accountId,
            connectionType,
            date,
            message,
            saveAccountCalendarNoteMutation,
            t,
            value,
        ]);

        const onCancel = useCallback(() => {
            setIsOpened(false);
        }, []);

        const addNote = useCallback(() => {
            setIsOpened(true);
        }, []);

        useEffect(() => {
            if (accountId && connectionType) {
                getAccountCalendarNoteQuery({
                    id: accountId,
                    connectionType,
                    date,
                });
            }
        }, [accountId, date, connectionType, getAccountCalendarNoteQuery]);

        useEffect(() => {
            if (isSuccess) {
                setIsOpened(false);
            }
        }, [isSuccess]);

        useSafariKeyboardPatches();

        return (
            <>
                {isFetching ? (
                    <Skeleton.Button
                        active
                        className={cn(className)}
                        size="large"
                    />
                ) : (
                    <Button
                        className={cn(className)}
                        icon={<BookOutlined />}
                        onClick={addNote}
                        size="large"
                        type="primary"
                    >
                        {t(
                            data?.note
                                ? 'tradingJournalNotesButtonNote'
                                : 'tradingJournalNotesButtonAddNote',
                        )}
                    </Button>
                )}
                <Drawer
                    closeIcon={null}
                    destroyOnClose
                    forceRender
                    height="85vh"
                    onClose={onCancel}
                    open={isOpened}
                    placement="bottom"
                    rootClassName={cls.root}
                    title={
                        <Flex
                            align="center"
                            justify="space-between"
                        >
                            <Typography.Text>
                                {t(
                                    data?.note
                                        ? 'tradingJournalNotesButtonNote'
                                        : 'tradingJournalNotesButtonAddNote',
                                )}
                            </Typography.Text>
                            <Button
                                className={cls.closeButton}
                                onClick={onCancel}
                                size="small"
                                type="link"
                            >
                                {t('tradingJournalCloseText')}
                            </Button>
                        </Flex>
                    }
                >
                    <Flex
                        className={cls.container}
                        justify="space-between"
                        vertical
                    >
                        <RichTextEditor
                            className={cls.editor}
                            onChange={setValue}
                            placeholder={t('tradingJournalNotesPlaceholder')}
                            value={data?.note ?? ''}
                        />

                        <Button
                            className={cn(cls.control, 'ql-save-button')}
                            loading={isLoading}
                            onClick={onSave}
                            size="large"
                            type="primary"
                        >
                            {t('tradingJournalNotesSaveButton')}
                        </Button>
                    </Flex>
                </Drawer>
            </>
        );
    },
);

TradingPlatformDailyInfoAsDrawerNotes.displayName =
    'TradingPlatformDailyInfoAsDrawerNotes';
